import type { GlobalState } from '../types';
import { MAIN_THREAD_ID } from '../../api/types';

import { SERVICE_NOTIFICATIONS_USER_ID } from '../../config';
import { isUserId } from '../../util/entities/ids';
import { getServerTime } from '../../util/serverTime';
import { formatElapsedTime } from '../../services/crmState';
import { getMessageAction } from '../helpers/messageMedia';
import { isUserBot } from '../helpers/users';
import { selectChat, selectChatLastMessage } from './chats';
import { selectThreadReadState } from './threads';
import { selectUser } from './users';

export function selectIsChatAwaitingReply(
  global: GlobalState,
  chatId: string,
  requireRead = false,
): boolean {
  if (!chatId || chatId === SERVICE_NOTIFICATIONS_USER_ID || chatId === global.currentUserId) {
    return false;
  }

  const chat = selectChat(global, chatId);
  if (!chat || chat.type === 'chatTypeChannel' || chat.isSupport || !isUserId(chatId)) {
    return false;
  }

  const user = selectUser(global, chatId);
  if (user && (isUserBot(user) || user.isSelf)) {
    return false;
  }

  const lastMessage = selectChatLastMessage(global, chatId, 'all');
  if (!lastMessage || lastMessage.isOutgoing || lastMessage.sendingState) {
    return false;
  }

  if (getMessageAction(lastMessage)) {
    return false;
  }

  const readState = selectThreadReadState(global, chatId, MAIN_THREAD_ID);
  const hasUnread = Boolean(readState?.unreadCount || readState?.hasUnreadMark);

  if (requireRead) {
    return !hasUnread;
  }

  return true;
}

export interface ChatSlaInfo {
  isAwaitingReply: boolean;
  isRead: boolean;
  elapsedMinutes: number;
  slaLevel: 'normal' | 'urgent';
  formattedTime: string;
}

export function selectChatSlaInfo(
  global: GlobalState,
  chatId: string,
  urgentMinutes = 45,
): ChatSlaInfo | undefined {
  const isAwaitingReply = selectIsChatAwaitingReply(global, chatId, false);
  if (!isAwaitingReply) {
    return undefined;
  }

  const isRead = selectIsChatAwaitingReply(global, chatId, true);
  const lastMessage = selectChatLastMessage(global, chatId, 'all');
  const date = lastMessage?.date || 0;
  const now = getServerTime();
  const elapsedSeconds = Math.max(0, now - date);
  const elapsedMinutes = Math.floor(elapsedSeconds / 60);
  const slaLevel: 'normal' | 'urgent' = elapsedMinutes >= urgentMinutes ? 'urgent' : 'normal';

  return {
    isAwaitingReply,
    isRead,
    elapsedMinutes,
    slaLevel,
    formattedTime: formatElapsedTime(elapsedSeconds),
  };
}

export function selectUnansweredChatsSummary(
  global: GlobalState,
  urgentMinutes = 45,
): { total: number; urgent: number } {
  const activeIds = global.chats.listIds.active;
  if (!activeIds || !activeIds.length) {
    return { total: 0, urgent: 0 };
  }

  let total = 0;
  let urgent = 0;
  const now = getServerTime();

  for (let i = 0; i < activeIds.length; i++) {
    const chatId = activeIds[i];
    if (selectIsChatAwaitingReply(global, chatId, false)) {
      total++;
      const lastMessage = selectChatLastMessage(global, chatId, 'all');
      if (lastMessage?.date) {
        const elapsedMinutes = Math.floor(Math.max(0, now - lastMessage.date) / 60);
        if (elapsedMinutes >= urgentMinutes) {
          urgent++;
        }
      }
    }
  }

  return { total, urgent };
}
